'use client';

import { c as _c } from "react/compiler-runtime";
import { ChefHat, Clock, ListOrdered, Star } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type { ReactNode } from 'react';
import { PALETTE } from '@app/lib/palette';
import type { RecipeCardData } from '@app/lib/recipe-card';
import { css, cx } from 'styled-system/css';
import { flex } from 'styled-system/patterns';
import { Badge } from '../atoms/Badge';
import { SmartImage } from '../atoms/SmartImage';

// ---------------------------------------------------------------------------
// Shared category color map (used by badge overlays and hover effects)
// ---------------------------------------------------------------------------

export const categoryColors: Record<string, string> = {
  Hauptgericht: PALETTE.orange,
  Beilage: PALETTE.emerald,
  Dessert: PALETTE.pink,
  Frühstück: PALETTE.gold,
  Getränk: PALETTE.blue,
  Vorspeise: PALETTE.purple,
  Fingerfood: PALETTE.orange,
  Brunch: '#fab1a0'
};

// ---------------------------------------------------------------------------
// Data shape — accepts RecipeCardData with optional overrides for consumers
// that don't have all fields (e.g. favorites without rating/time)
// ---------------------------------------------------------------------------

export type RecipeCardRecipe = Partial<RecipeCardData> & Pick<RecipeCardData, 'id' | 'slug' | 'title' | 'image' | 'category'>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RecipeCardProps {
  recipe: RecipeCardRecipe;
  /** "default" — full grid card; "compact" — small horizontal scroll item; "list" — full-width horizontal row */
  variant?: 'default' | 'compact' | 'list';
  /** Show category badge on image with colored background (default: false — shows in content) */
  categoryOnImage?: boolean;
  /** Make category badge a clickable link (requires categorySlug) */
  categoryLink?: boolean;
  /** Hide category badge entirely */
  hideCategory?: boolean;
  /** Show 5-star visual rating instead of single star + number */
  starRating?: boolean;
  /** Extra content rendered after the card footer (e.g. "Saved X ago" row) */
  footer?: ReactNode;
  /** Extra overlay on the image (e.g. status icon, time badge) */
  imageOverlay?: ReactNode;
  className?: string;
}

// ---------------------------------------------------------------------------
// Star rating sub-component
// ---------------------------------------------------------------------------

function StarRating(t0) {
  const $ = _c(11);
  const {
    rating
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = css({
      display: "flex",
      gap: "1",
      alignItems: "center"
    });
    t2 = css({
      display: "flex",
      gap: "0.5"
    });
    t3 = [1, 2, 3, 4, 5];
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== rating) {
    t4 = <div className={t2}>{t3.map(v => <Star key={v} size={14} className={css({
        color: v <= Math.floor(rating) ? "palette.gold" : "#e0e0e0"
      })} />)}</div>;
    $[3] = rating;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = css({
      fontSize: "xs",
      color: "text-muted"
    });
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== rating) {
    t6 = <span className={t5}>{rating}</span>;
    $[6] = rating;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t6) {
    t7 = <div className={t1}>{t4}{t6}</div>;
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
function SingleStar(t0) {
  const $ = _c(4);
  const {
    rating
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = flex({
      align: "center",
      gap: "1"
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Star size={14} className={css({
      color: "palette.gold"
    })} />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== rating) {
    t3 = <span className={t1}>{t2}<span>{rating}</span></span>;
    $[2] = rating;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

// ---------------------------------------------------------------------------
// Category badge rendered on image overlay
// ---------------------------------------------------------------------------

function CategoryOverlay(t0) {
  const $ = _c(18);
  const {
    recipe,
    link
  } = t0;
  const router = useRouter();
  const color = recipe.categoryColor || categoryColors[recipe.category] || PALETTE.orange;
  let t1;
  if ($[0] !== link) {
    t1 = css({
      position: "absolute",
      top: "2",
      left: "2",
      color: "white",
      padding: "2px 10px",
      minHeight: "24px",
      display: "inline-flex",
      alignItems: "center",
      borderRadius: "full",
      fontSize: "0.7rem",
      fontWeight: "600",
      textTransform: "uppercase",
      letterSpacing: "wide",
      boxShadow: {
        base: "0 2px 8px rgba(0,0,0,0.2)",
        _dark: "0 2px 8px rgba(0,0,0,0.5)"
      },
      border: "none",
      cursor: link ? "pointer" : "default",
      transition: "all 150ms ease",
      _hover: link ? {
        opacity: 0.85,
        transform: "scale(1.05)"
      } : {}
    });
    $[0] = link;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const badgeStyle = t1;
  if (link && recipe.categorySlug) {
    let t2;
    if ($[2] !== recipe.categorySlug || $[3] !== router) {
      t2 = e => {
        e.stopPropagation();
        e.preventDefault();
        router.push(`/category/${recipe.categorySlug}`);
      };
      $[2] = recipe.categorySlug;
      $[3] = router;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== color) {
      t3 = {
        background: color
      };
      $[5] = color;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== badgeStyle || $[8] !== recipe.category || $[9] !== t2 || $[10] !== t3) {
      t4 = <button type="button" onClick={t2} className={badgeStyle} style={t3}>{recipe.category}</button>;
      $[7] = badgeStyle;
      $[8] = recipe.category;
      $[9] = t2;
      $[10] = t3;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    return t4;
  }
  let t2;
  if ($[12] !== color) {
    t2 = {
      background: color
    };
    $[12] = color;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== badgeStyle || $[15] !== recipe.category || $[16] !== t2) {
    t3 = <span className={badgeStyle} style={t2}>{recipe.category}</span>;
    $[14] = badgeStyle;
    $[15] = recipe.category;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
}

// ---------------------------------------------------------------------------
// RecipeCard
// ---------------------------------------------------------------------------

export function RecipeCard(t0) {
  const $ = _c(149);
  const {
    recipe,
    variant: t1,
    categoryOnImage: t2,
    categoryLink: t3,
    hideCategory: t4,
    starRating: t5,
    footer,
    imageOverlay,
    className
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const categoryOnImage = t2 === undefined ? false : t2;
  const categoryLink = t3 === undefined ? false : t3;
  const hideCategory = t4 === undefined ? false : t4;
  const starRating = t5 === undefined ? false : t5;
  const isCompact = variant === "compact";
  const isList = variant === "list";
  const color = recipe.categoryColor || categoryColors[recipe.category] || PALETTE.orange;
  if (isList) {
    let t6;
    if ($[0] !== className || $[1] !== color) {
      t6 = cx(css({
        bg: "surface.elevated",
        borderRadius: "xl",
        overflow: "hidden",
        border: "1px solid",
        borderColor: "rgba(0,0,0,0.06)",
        transition: "all 200ms ease",
        cursor: "pointer",
        display: "flex",
        flexDirection: "row",
        _hover: {
          borderColor: color,
          boxShadow: `0 4px 16px ${color}18`
        }
      }), className);
      $[0] = className;
      $[1] = color;
      $[2] = t6;
    } else {
      t6 = $[2];
    }
    let t7;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = css({
        position: "relative",
        flexShrink: "0",
        width: "120px",
        aspectRatio: "4/3",
        overflow: "hidden"
      });
      $[3] = t7;
    } else {
      t7 = $[3];
    }
    const t8 = recipe.image ?? undefined;
    const t9 = recipe.title;
    const t10 = recipe.id;
    let t11;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = css({
        objectFit: "cover"
      });
      $[4] = t11;
    } else {
      t11 = $[4];
    }
    let t12;
    if ($[5] !== recipe.id || $[6] !== recipe.title || $[7] !== t11 || $[8] !== t8) {
      t12 = <SmartImage src={t8} alt={t9} fill={true} recipeId={t10} sizes="120px" className={t11} />;
      $[5] = recipe.id;
      $[6] = recipe.title;
      $[7] = t11;
      $[8] = t8;
      $[9] = t12;
    } else {
      t12 = $[9];
    }
    let t13;
    if ($[10] !== imageOverlay || $[11] !== t12 || $[12] !== t7) {
      t13 = <div className={t7}>{t12}{imageOverlay}</div>;
      $[10] = imageOverlay;
      $[11] = t12;
      $[12] = t7;
      $[13] = t13;
    } else {
      t13 = $[13];
    }
    let t14;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = css({
        flex: "1",
        minWidth: "0",
        px: "3.5",
        py: "3",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "1"
      });
      $[14] = t14;
    } else {
      t14 = $[14];
    }
    let t15;
    if ($[15] !== hideCategory || $[16] !== recipe.category) {
      t15 = !hideCategory && <div className={css({
        alignSelf: "flex-start"
      })}><Badge>{recipe.category}</Badge></div>;
      $[15] = hideCategory;
      $[16] = recipe.category;
      $[17] = t15;
    } else {
      t15 = $[17];
    }
    let t16;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = css({
        fontWeight: "700",
        fontFamily: "heading",
        color: "text",
        fontSize: "sm",
        lineHeight: "tight",
        lineClamp: 1
      });
      $[18] = t16;
    } else {
      t16 = $[18];
    }
    let t17;
    if ($[19] !== recipe.title || $[20] !== t16) {
      t17 = <h3 className={t16}>{recipe.title}</h3>;
      $[19] = recipe.title;
      $[20] = t16;
      $[21] = t17;
    } else {
      t17 = $[21];
    }
    let t18;
    if ($[22] !== recipe.description) {
      t18 = recipe.description && <p className={css({
        fontSize: "xs",
        color: "text-muted",
        lineClamp: 1,
        lineHeight: "1.4"
      })}>{recipe.description}</p>;
      $[22] = recipe.description;
      $[23] = t18;
    } else {
      t18 = $[23];
    }
    let t19;
    if ($[24] !== t14 || $[25] !== t15 || $[26] !== t17 || $[27] !== t18) {
      t19 = <div className={t14}>{t15}{t17}{t18}</div>;
      $[24] = t14;
      $[25] = t15;
      $[26] = t17;
      $[27] = t18;
      $[28] = t19;
    } else {
      t19 = $[28];
    }
    let t20;
    let t21;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = css({
        flexShrink: "0",
        px: "3.5",
        py: "3",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: "2",
        borderLeft: "1px solid",
        borderColor: "rgba(255,255,255,0.05)",
        minWidth: "110px"
      });
      t21 = flex({
        align: "center",
        gap: "1",
        fontSize: "xs",
        color: "text-muted"
      });
      $[29] = t20;
      $[30] = t21;
    } else {
      t20 = $[29];
      t21 = $[30];
    }
    let t22;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <Star size={12} className={css({
        color: "palette.gold"
      })} />;
      $[31] = t22;
    } else {
      t22 = $[31];
    }
    const t23 = recipe.rating != null && recipe.rating > 0 ? recipe.rating : "\u2014";
    let t24;
    if ($[32] !== t21 || $[33] !== t22 || $[34] !== t23) {
      t24 = <span className={t21}>{t22}{t23}</span>;
      $[32] = t21;
      $[33] = t22;
      $[34] = t23;
      $[35] = t24;
    } else {
      t24 = $[35];
    }
    let t25;
    if ($[36] !== recipe.time) {
      t25 = recipe.time && <span className={flex({
        align: "center",
        gap: "1",
        fontSize: "xs",
        color: "text-muted"
      })}><Clock size={12} />{recipe.time}</span>;
      $[36] = recipe.time;
      $[37] = t25;
    } else {
      t25 = $[37];
    }
    let t26;
    if ($[38] !== recipe.stepCount) {
      t26 = recipe.stepCount != null && <span className={flex({
        align: "center",
        gap: "1",
        fontSize: "xs",
        color: "text-muted"
      })}><ListOrdered size={12} />{recipe.stepCount}</span>;
      $[38] = recipe.stepCount;
      $[39] = t26;
    } else {
      t26 = $[39];
    }
    let t27;
    if ($[40] !== recipe.difficulty) {
      t27 = recipe.difficulty && <span className={flex({
        align: "center",
        gap: "1",
        fontSize: "xs",
        color: "text-muted"
      })}><ChefHat size={12} />{recipe.difficulty}</span>;
      $[40] = recipe.difficulty;
      $[41] = t27;
    } else {
      t27 = $[41];
    }
    let t28;
    if ($[42] !== t20 || $[43] !== t24 || $[44] !== t25 || $[45] !== t26 || $[46] !== t27) {
      t28 = <div className={t20}>{t24}{t25}{t26}{t27}</div>;
      $[42] = t20;
      $[43] = t24;
      $[44] = t25;
      $[45] = t26;
      $[46] = t27;
      $[47] = t28;
    } else {
      t28 = $[47];
    }
    let t29;
    if ($[48] !== t13 || $[49] !== t19 || $[50] !== t28 || $[51] !== t6) {
      t29 = <div className={t6}>{t13}{t19}{t28}</div>;
      $[48] = t13;
      $[49] = t19;
      $[50] = t28;
      $[51] = t6;
      $[52] = t29;
    } else {
      t29 = $[52];
    }
    const listCard = t29;
    if (footer) {
      let t30;
      if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
        t30 = css({
          display: "flex",
          flexDir: "column",
          gap: "2"
        });
        $[53] = t30;
      } else {
        t30 = $[53];
      }
      const t31 = `/recipe/${recipe.slug}`;
      let t32;
      if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
        t32 = css({
          textDecoration: "none",
          color: "inherit"
        });
        $[54] = t32;
      } else {
        t32 = $[54];
      }
      let t33;
      if ($[55] !== listCard || $[56] !== t31 || $[57] !== t32) {
        t33 = <Link href={t31} className={t32}>{listCard}</Link>;
        $[55] = listCard;
        $[56] = t31;
        $[57] = t32;
        $[58] = t33;
      } else {
        t33 = $[58];
      }
      let t34;
      if ($[59] !== footer || $[60] !== t30 || $[61] !== t33) {
        t34 = <div className={t30}>{t33}{footer}</div>;
        $[59] = footer;
        $[60] = t30;
        $[61] = t33;
        $[62] = t34;
      } else {
        t34 = $[62];
      }
      return t34;
    }
    const t30 = `/recipe/${recipe.slug}`;
    let t31;
    if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = css({
        textDecoration: "none",
        color: "inherit"
      });
      $[63] = t31;
    } else {
      t31 = $[63];
    }
    let t32;
    if ($[64] !== listCard || $[65] !== t30 || $[66] !== t31) {
      t32 = <Link href={t30} className={t31}>{listCard}</Link>;
      $[64] = listCard;
      $[65] = t30;
      $[66] = t31;
      $[67] = t32;
    } else {
      t32 = $[67];
    }
    return t32;
  }
  let t6;
  if ($[68] !== className || $[69] !== color || $[70] !== isCompact) {
    t6 = cx(css({
      bg: "surface.elevated",
      borderRadius: "xl",
      overflow: "hidden",
      border: "1px solid",
      borderColor: "rgba(0,0,0,0.06)",
      transition: "all 200ms ease",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      height: "100%",
      _hover: {
        transform: "translateY(-4px)",
        borderColor: color,
        boxShadow: `0 12px 28px ${color}20`
      }
    }), isCompact && css({
      flex: "0 0 auto",
      width: "200px"
    }), className);
    $[68] = className;
    $[69] = color;
    $[70] = isCompact;
    $[71] = t6;
  } else {
    t6 = $[71];
  }
  const t7 = isCompact ? "16/10" : "16/10";
  let t8;
  if ($[72] !== t7) {
    t8 = css({
      position: "relative",
      aspectRatio: t7,
      overflow: "hidden"
    });
    $[72] = t7;
    $[73] = t8;
  } else {
    t8 = $[73];
  }
  const t9 = recipe.image ?? undefined;
  const t10 = recipe.title;
  const t11 = recipe.id;
  let t12;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = css({
      objectFit: "cover"
    });
    $[74] = t12;
  } else {
    t12 = $[74];
  }
  let t13;
  if ($[75] !== recipe.id || $[76] !== recipe.title || $[77] !== t12 || $[78] !== t9) {
    t13 = <SmartImage src={t9} alt={t10} fill={true} recipeId={t11} sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw" className={t12} />;
    $[75] = recipe.id;
    $[76] = recipe.title;
    $[77] = t12;
    $[78] = t9;
    $[79] = t13;
  } else {
    t13 = $[79];
  }
  let t14;
  if ($[80] !== categoryLink || $[81] !== categoryOnImage || $[82] !== hideCategory || $[83] !== recipe) {
    t14 = !hideCategory && categoryOnImage && <CategoryOverlay recipe={recipe} link={categoryLink} />;
    $[80] = categoryLink;
    $[81] = categoryOnImage;
    $[82] = hideCategory;
    $[83] = recipe;
    $[84] = t14;
  } else {
    t14 = $[84];
  }
  let t15;
  if ($[85] !== imageOverlay || $[86] !== t13 || $[87] !== t14 || $[88] !== t8) {
    t15 = <div className={t8}>{t13}{t14}{imageOverlay}</div>;
    $[85] = imageOverlay;
    $[86] = t13;
    $[87] = t14;
    $[88] = t8;
    $[89] = t15;
  } else {
    t15 = $[89];
  }
  const t16 = isCompact ? "3" : "4";
  let t17;
  if ($[90] !== t16) {
    t17 = css({
      p: t16,
      flex: "1",
      display: "flex",
      flexDirection: "column"
    });
    $[90] = t16;
    $[91] = t17;
  } else {
    t17 = $[91];
  }
  let t18;
  if ($[92] !== categoryOnImage || $[93] !== hideCategory || $[94] !== recipe.category) {
    t18 = !hideCategory && !categoryOnImage && <div className={css({
      mb: "2"
    })}><Badge>{recipe.category}</Badge></div>;
    $[92] = categoryOnImage;
    $[93] = hideCategory;
    $[94] = recipe.category;
    $[95] = t18;
  } else {
    t18 = $[95];
  }
  const t19 = isCompact ? "600" : "700";
  const t20 = isCompact ? "sm" : "base";
  const t21 = isCompact ? 1 : 2;
  const t22 = isCompact ? "1" : undefined;
  let t23;
  if ($[96] !== t19 || $[97] !== t20 || $[98] !== t21 || $[99] !== t22) {
    t23 = css({
      fontWeight: t19,
      fontFamily: "heading",
      color: "text",
      fontSize: t20,
      lineHeight: "tight",
      lineClamp: t21,
      mb: t22
    });
    $[96] = t19;
    $[97] = t20;
    $[98] = t21;
    $[99] = t22;
    $[100] = t23;
  } else {
    t23 = $[100];
  }
  let t24;
  if ($[101] !== recipe.title || $[102] !== t23) {
    t24 = <h3 className={t23}>{recipe.title}</h3>;
    $[101] = recipe.title;
    $[102] = t23;
    $[103] = t24;
  } else {
    t24 = $[103];
  }
  let t25;
  if ($[104] !== isCompact || $[105] !== recipe.description) {
    t25 = !isCompact && recipe.description && <p className={css({
      fontFamily: "body",
      fontSize: "sm",
      color: "text-muted",
      mt: "1",
      lineClamp: 2,
      lineHeight: "1.5"
    })}>{recipe.description}</p>;
    $[104] = isCompact;
    $[105] = recipe.description;
    $[106] = t25;
  } else {
    t25 = $[106];
  }
  const t26 = isCompact ? undefined : "3";
  const t27 = isCompact ? "0.75rem" : "sm";
  let t28;
  if ($[107] !== t26 || $[108] !== t27) {
    t28 = flex({
      justify: "space-between",
      align: "center",
      mt: "auto",
      pt: t26,
      fontFamily: "body",
      fontSize: t27,
      color: "text-muted"
    });
    $[107] = t26;
    $[108] = t27;
    $[109] = t28;
  } else {
    t28 = $[109];
  }
  let t29;
  if ($[110] !== recipe.rating || $[111] !== starRating) {
    t29 = starRating ? recipe.rating != null && recipe.rating > 0 ? <StarRating rating={recipe.rating} /> : <span className={flex({
      align: "center",
      gap: "1"
    })}><Star size={14} className={css({
        color: "palette.gold"
      })} /><span className={css({
        fontSize: "xs",
        color: "text-muted"
      })}>—</span></span> : recipe.rating != null && recipe.rating > 0 ? <SingleStar rating={recipe.rating} /> : <span className={flex({
      align: "center",
      gap: "1"
    })}><Star size={14} className={css({
        color: "palette.gold"
      })} /><span>—</span></span>;
    $[110] = recipe.rating;
    $[111] = starRating;
    $[112] = t29;
  } else {
    t29 = $[112];
  }
  let t30;
  if ($[113] !== isCompact || $[114] !== recipe.stepCount) {
    t30 = !isCompact && recipe.stepCount != null && <span className={flex({
      align: "center",
      gap: "1"
    })}><ListOrdered size={14} className={css({
        color: "foreground.muted"
      })} />{recipe.stepCount}</span>;
    $[113] = isCompact;
    $[114] = recipe.stepCount;
    $[115] = t30;
  } else {
    t30 = $[115];
  }
  let t31;
  if ($[116] !== isCompact || $[117] !== recipe.time) {
    t31 = recipe.time && <span className={flex({
      align: "center",
      gap: "1"
    })}>{isCompact ? null : <Clock size={14} className={css({
        color: "foreground.muted"
      })} />}{recipe.time}</span>;
    $[116] = isCompact;
    $[117] = recipe.time;
    $[118] = t31;
  } else {
    t31 = $[118];
  }
  let t32;
  if ($[119] !== t28 || $[120] !== t29 || $[121] !== t30 || $[122] !== t31) {
    t32 = <div className={t28}>{t29}{t30}{t31}</div>;
    $[119] = t28;
    $[120] = t29;
    $[121] = t30;
    $[122] = t31;
    $[123] = t32;
  } else {
    t32 = $[123];
  }
  let t33;
  if ($[124] !== t17 || $[125] !== t18 || $[126] !== t24 || $[127] !== t25 || $[128] !== t32) {
    t33 = <div className={t17}>{t18}{t24}{t25}{t32}</div>;
    $[124] = t17;
    $[125] = t18;
    $[126] = t24;
    $[127] = t25;
    $[128] = t32;
    $[129] = t33;
  } else {
    t33 = $[129];
  }
  let t34;
  if ($[130] !== t15 || $[131] !== t33 || $[132] !== t6) {
    t34 = <div className={t6}>{t15}{t33}</div>;
    $[130] = t15;
    $[131] = t33;
    $[132] = t6;
    $[133] = t34;
  } else {
    t34 = $[133];
  }
  const card = t34;
  if (footer) {
    let t35;
    if ($[134] === Symbol.for("react.memo_cache_sentinel")) {
      t35 = css({
        display: "flex",
        flexDir: "column",
        gap: "2"
      });
      $[134] = t35;
    } else {
      t35 = $[134];
    }
    const t36 = `/recipe/${recipe.slug}`;
    let t37;
    if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
      t37 = css({
        textDecoration: "none",
        color: "inherit"
      });
      $[135] = t37;
    } else {
      t37 = $[135];
    }
    let t38;
    if ($[136] !== card || $[137] !== t36 || $[138] !== t37) {
      t38 = <Link href={t36} className={t37}>{card}</Link>;
      $[136] = card;
      $[137] = t36;
      $[138] = t37;
      $[139] = t38;
    } else {
      t38 = $[139];
    }
    let t39;
    if ($[140] !== footer || $[141] !== t35 || $[142] !== t38) {
      t39 = <div className={t35}>{t38}{footer}</div>;
      $[140] = footer;
      $[141] = t35;
      $[142] = t38;
      $[143] = t39;
    } else {
      t39 = $[143];
    }
    return t39;
  }
  const t35 = `/recipe/${recipe.slug}`;
  let t36;
  if ($[144] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = css({
      textDecoration: "none",
      color: "inherit"
    });
    $[144] = t36;
  } else {
    t36 = $[144];
  }
  let t37;
  if ($[145] !== card || $[146] !== t35 || $[147] !== t36) {
    t37 = <Link href={t35} className={t36}>{card}</Link>;
    $[145] = card;
    $[146] = t35;
    $[147] = t36;
    $[148] = t37;
  } else {
    t37 = $[148];
  }
  return t37;
}